import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import new components
import OrderHeader from './OrderHeader';
import OrderForm from './OrderForm';
import OrderFooter from './OrderFooter';
import OrderSuccessDialog from './OrderSuccessDialog';

// Import services
import { getmarginValues, placeOrder, placeOrderformportfolio } from "../../services/allApiService";
import { getClientData } from "../custom";
import { clientPositionsGet } from '../../actions/portfolioActions';

// Import Redux actions
import {
    startMarketDataPolling,
    stopMarketDataPolling,
    fetchMarketDataRequest
} from '../../store/reducers/addorderReducer';

function AddOrder() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {

        const interval = setInterval(() => {
            dispatch(clientPositionsGet());
        }, 3000);

        return () => clearInterval(interval);

    }, []);
    // Common paths are: state.addorder, state.market, state.addOrder
    const marketData = useSelector(state => state.addorder?.marketData || state.market?.marketData || []);

    const {
        m_buttonType, m_scriptName, m_exchange, m_price, m_lotssize, m_qty,
        m_netChange, m_percentChange, m_symbolToken, m_activeTabValue,
        m_expiry, m_lowerCircuit, m_upperCircuit, m_high, m_low,
        m_script_id, m_exchange_id
    } = location.state || {};
    const {
        o_buttonType, o_scriptName, o_exchange, o_price, o_lotssize,
        o_netChange, o_percentChange, o_script_id, o_exchange_id,
        o_order_id, o_qty, o_expiry, o_token, o_high, o_low,
        o_upperCircuit, o_lowerCircuit, o_from_portfolio, o_script_name
    } = location.state || {};


    // State management
    const [activeTab, setActiveTab] = useState('market');
    const [inputPrice, setInputPrice] = useState(null);
    const [qtyValue, setQtyValue] = useState(null);
    const [lotsValue, setLotsValue] = useState(1.00);
    const [orderPlacedDialog, setOrderPlacedDialog] = useState(false);
    const [saveOrder, setSaveOrder] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Financial state
    const [marginData, setMarginData] = useState(1);
    const [marginOvernightData, setMarginOvernightData] = useState(null);
    const [marginOvernightDataLive, setMarginOvernightDataLive] = useState(null);
    const [marginDataLive, setMarginDataLive] = useState(null);
    const [orderWarning, setOrderWarning] = useState('');
    const parsedUserData = getClientData();

    // Determine data source
    const isOrderExisting = o_order_id != null;
    const currentSymbolToken = isOrderExisting ? o_token : m_symbolToken;
    const currentExchange = isOrderExisting ? o_exchange : m_exchange;

    // Fixed helper functions to get data from Redux store
    const getSymbolPrice = (symbolToken) => {

        const symbol = marketData.find(item =>
            String(item?.symbolToken) === String(symbolToken)
        );

        let price = m_buttonType === 'buy' ? symbol?.depth.sell[0]?.price : symbol?.depth.buy[0]?.price;

        return symbol ? (price || symbol.ltp) : null;
    };

    const getSymbolData = (symbolToken) => {
        if (!symbolToken || !marketData || marketData.length === 0) {
            return null;
        }

        return marketData.find(item =>
            String(item?.symbolToken) === String(symbolToken)
        );
    };

    // Get live price from Redux store - FIXED
    const livePrice = getSymbolPrice(currentSymbolToken);
    const symbolData = getSymbolData(currentSymbolToken);

    const currentData = {
        buttonType: isOrderExisting ? o_buttonType : m_buttonType,
        script_id: isOrderExisting ? o_script_id : m_script_id,
        scriptName: isOrderExisting ? o_scriptName : m_scriptName,
        exchange: currentExchange,
        price: livePrice || (isOrderExisting ? o_price : m_price),
        lotssize: isOrderExisting ? o_lotssize : m_lotssize,
        qty: isOrderExisting ? o_qty : m_qty,
        netChange: symbolData?.netChange || (isOrderExisting ? o_netChange : m_netChange),
        percentChange: symbolData?.percentChange || (isOrderExisting ? o_percentChange : m_percentChange),
    };

    // fatch margin value
    useEffect(() => {
        if (!currentData) return;

        const fetchMargin = async () => {
            try {

                const data = await getmarginValues(currentData.script_id);

                setMarginData(data?.data?.margin_balance);
                setMarginOvernightData(data?.data?.margin_overnight_balance);
            } catch (err) {
                console.error('Margin API error', err);
            }
        };
        fetchMargin();
    }, []);

    // Start market data polling on component mount
    useEffect(() => {
        if (currentSymbolToken && currentExchange) {
            const symbolTokensArray = [{
                token: currentSymbolToken,
                script_id: isOrderExisting ? o_script_id : m_script_id,
                exchange_id: isOrderExisting ? o_exchange_id : m_exchange_id,
                id: null
            }];

            // Start polling for market data every 3 seconds
            dispatch(startMarketDataPolling(currentExchange, symbolTokensArray, 1000));

            // Fetch initial data
            dispatch(fetchMarketDataRequest(currentExchange, symbolTokensArray));
        }

        // Cleanup: Stop polling when component unmounts
        return () => {
            dispatch(stopMarketDataPolling());
        };
    }, [currentSymbolToken, currentExchange, dispatch, isOrderExisting, o_script_id, m_script_id, o_exchange_id, m_exchange_id]);

    // Update input price when live price changes (only for market orders)
    useEffect(() => {
        if (activeTab === 'market' && livePrice && String(livePrice) !== String(inputPrice)) {
            setInputPrice(livePrice);
        }
    }, [livePrice, activeTab, inputPrice]);

    // Initialize price and quantity
    useEffect(() => {
        if (!inputPrice && currentData.price) {
            setInputPrice(currentData.price);
        }
        const initialQty = isOrderExisting ? o_qty : currentData.qty;
        if (!qtyValue) {
            setQtyValue(initialQty);
        }
        setLotsValue(currentData.lotssize);
        updateFinancialCalculations(inputPrice, initialQty, currentData.lotssize);

    }, []);

    // Update price when tab changes
    const handleTabChange = (newTab) => {
        setActiveTab(newTab);

        if (newTab === 'market' && livePrice) {
            setInputPrice(livePrice);
        } else if (newTab === 'limit' && !inputPrice) {
            setInputPrice(currentData.price);
        } else if (newTab === 'stoploss' && !inputPrice) {
            setInputPrice(currentData.price);
        }
    };
    useEffect(() => {
        if (activeTab === 'market' && livePrice && qtyValue) {
            updateFinancialCalculations(livePrice, qtyValue);
        }
    }, [livePrice, qtyValue, activeTab, marginData, marginOvernightData]);

    // Financial calculations
    const updateFinancialCalculations = (price, qty) => {
        if (!price || !qty || qty <= 0) {
            setMarginDataLive(0);
            setMarginOvernightDataLive(0);
            return;
        }

        // Intraday margin
        if (marginData) {
            setMarginDataLive((price * qty) / marginData);
        }

        // Overnight margin
        if (marginOvernightData) {
            setMarginOvernightDataLive((price * qty) / marginOvernightData);
        }
    };


    // Single source of truth for the qty <-> lots relationship.
    // Shares per lot is derived from the script's base values so both
    // directions (qty->lots and lots->qty) always stay consistent.
    const getQtyPerLot = () => {
        const baseQty = Number(currentData.qty) || 1;
        const baseLots = Number(currentData.lotssize) || 1;
        return baseLots > 0 ? baseQty / baseLots : baseQty;
    };

    const qtyToLots = (qty) => {
        const qtyPerLot = getQtyPerLot();
        return qtyPerLot > 0 ? qty / qtyPerLot : 0;
    };

    const lotsToQty = (lots) => {
        return Math.round(lots * getQtyPerLot());
    };

    const priceForCalc = () => (activeTab === 'market' ? livePrice : inputPrice);


    const handleQtyChange = (e) => {
        const newValue = e.target.value;

        // Allow empty string while typing
        if (newValue === '') {
            setQtyValue('');
            setLotsValue('');
            updateFinancialCalculations(priceForCalc(), 0);
            return;
        }

        // Block invalid inputs
        if (isNaN(newValue)) return;

        const qty = Number(newValue);
        if (qty <= 0) return;

        const newLots = qtyToLots(qty);
        setQtyValue(qty);
        setLotsValue(Number(newLots.toFixed(2)));
        updateFinancialCalculations(priceForCalc(), qty);
    };


    // Lots is now editable. Typing lots recomputes qty from the same ratio.
    const handleLotsChange = (e) => {
        const newValue = e.target.value;

        // Allow empty / partial input (e.g. "1.") while typing.
        if (newValue === '' || newValue === '.') {
            setLotsValue(newValue);
            setQtyValue('');
            updateFinancialCalculations(priceForCalc(), 0);
            return;
        }

        if (isNaN(newValue)) return;

        const lots = Number(newValue);
        if (lots < 0) return;

        const newQty = lotsToQty(lots);
        setLotsValue(newValue); // keep raw text so decimals type smoothly
        setQtyValue(newQty);
        updateFinancialCalculations(priceForCalc(), newQty);
    };


    const handleQtyIncrement = () => {
        const newQty = (Number(qtyValue) || 0) + 1;

        setQtyValue(newQty);
        setLotsValue(Number(qtyToLots(newQty).toFixed(2)));
        updateFinancialCalculations(priceForCalc(), newQty);
    };


    const handleQtyDecrement = () => {
        const currentQty = Number(qtyValue) || 1;

        if (currentQty > 1) {
            const newQty = currentQty - 1;

            setQtyValue(newQty);
            setLotsValue(Number(qtyToLots(newQty).toFixed(2)));
            updateFinancialCalculations(priceForCalc(), newQty);
        }
    };


    const handleLotsIncrement = () => {
        const currentLots = Number(lotsValue) || 0;
        const step = currentLots >= 1.00 ? 1.00 : 0.01;
        const newLots = Number((currentLots + step).toFixed(2));

        const newQty = lotsToQty(newLots);

        setLotsValue(newLots);
        setQtyValue(newQty);
        updateFinancialCalculations(priceForCalc(), newQty);
    };

    const handleLotsDecrement = () => {
        const currentLots = Number(lotsValue) || 1;
        const step = currentLots > 1.00 ? 1.00 : 0.01;
        const newLots = Number(Math.max(currentLots - step, 0.01).toFixed(2));

        const newQty = lotsToQty(newLots);

        setLotsValue(newLots);
        setQtyValue(newQty);
        updateFinancialCalculations(priceForCalc(), newQty);
    };


    const handleSwipeComplete = async () => {
        if (isLoading) return;
        setIsLoading(true);
        dispatch(stopMarketDataPolling());

        try {
            const finalPrice = activeTab === 'market' ? (livePrice || inputPrice) : inputPrice;
            const finalQty = Number(qtyValue);
            const finalLots = Number(lotsValue);

            if (finalPrice <= 0 || !finalQty || finalQty <= 0) {
                toast.error("Quantity and price should be greater than 0");
                return;
            }

            if (!finalLots || finalLots <= 0) {
                toast.error("Lots should be greater than 0");
                return;
            }

            const orderParams = {
                script_id: isOrderExisting ? o_script_id : m_script_id,
                exchange_id: isOrderExisting ? o_exchange_id : m_exchange_id,
                buttonType: currentData.buttonType,
                price: finalPrice,
                lotSize: finalLots,
                qty: finalQty,
                order_type: activeTab,
                orderId: isOrderExisting ? o_order_id : null,
                squareOff: isOrderExisting ? '1' : null,
                expiry: isOrderExisting ? o_expiry : m_expiry,
                token: currentSymbolToken,
                circuit_low: symbolData?.lowerCircuit || (isOrderExisting ? o_lowerCircuit : m_lowerCircuit),
                circuit_high: symbolData?.upperCircuit || (isOrderExisting ? o_upperCircuit : m_upperCircuit),
                high: symbolData?.high || (isOrderExisting ? o_high : m_high),
                low: symbolData?.low || (isOrderExisting ? o_low : m_low),
                from_portfolio: o_from_portfolio ?? null,
                client_id: parsedUserData?.client_id,
                script_name: isOrderExisting ? o_script_name : null,
                marketprice: livePrice
            };

            let response;
            if (orderParams.from_portfolio === 'portfolio') {
                response = await placeOrderformportfolio(
                    orderParams.script_id, orderParams.price, orderParams.lotSize,
                    orderParams.qty, orderParams.exchange_id, orderParams.buttonType,
                    orderParams.order_type, orderParams.expiry, orderParams.token,
                    orderParams.orderId, orderParams.squareOff, orderParams.circuit_low,
                    orderParams.circuit_high, orderParams.high, orderParams.low,
                    null, orderParams.client_id, orderParams.script_name, orderParams.marketprice
                );
            } else {
                response = await placeOrder(
                    orderParams.script_id, orderParams.price, orderParams.lotSize,
                    orderParams.qty, orderParams.exchange_id, orderParams.buttonType,
                    orderParams.order_type, orderParams.expiry, orderParams.token,
                    orderParams.orderId, orderParams.squareOff, orderParams.circuit_low,
                    orderParams.circuit_high, orderParams.high, orderParams.low,
                    null, orderParams.marketprice
                );
            }

            if (response && response.success) {
                setSaveOrder(response?.data || []);
                setOrderWarning(response?.warning || '');
                setOrderPlacedDialog(true);

                dispatch(stopMarketDataPolling());
            } else {
                toast.error(response?.message || "Failed to place order.");
            }
        } catch (error) {
            console.error('Order placement error:', error);
            toast.error("An error occurred while placing the order.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setOrderPlacedDialog(false);
        dispatch(stopMarketDataPolling());
        navigate("/watchlist");
    };

    useEffect(() => {

        if (orderPlacedDialog && !orderWarning) {
            const timer = setTimeout(() => {
                handleClose();
            }, 1500);

            return () => clearTimeout(timer);
        }
    }, [orderPlacedDialog]);

    const handleGoBack = () => {
        dispatch(stopMarketDataPolling());
        navigate(-1);
    };

    // if (!currentData.buttonType) {
    //     return <div>No type provided</div>;
    // }

    return (
        <div className="flex items-center justify-center flex-col bg-black min-h-screen">
            <div className="h-[100vh] flex-grow max-w-sm min-w-[370px] w-full bg-gray-100
        [&::-webkit-scrollbar]:w-1 
        [&::-webkit-scrollbar-track]:bg-transparent 
        [&::-webkit-scrollbar-thumb]:bg-transparent 
        scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent">

                <OrderHeader
                    scriptName={currentData.scriptName}
                    exchange={currentData.exchange}
                    price={currentData.price}
                    lotsSize={currentData.lotssize}
                    netChange={currentData.netChange}
                    percentChange={currentData.percentChange}
                    buttonType={currentData.buttonType}
                    onGoBack={handleGoBack}
                />

                <OrderForm
                    buttonType={currentData.buttonType}
                    activeTab={activeTab}
                    setActiveTab={handleTabChange}
                    inputPrice={inputPrice}
                    setInputPrice={setInputPrice}
                    qtyValue={qtyValue}
                    handleQtyChange={handleQtyChange}
                    handleQtyIncrement={handleQtyIncrement}
                    handleQtyDecrement={handleQtyDecrement}
                    lotsValue={lotsValue}
                    handleLotsChange={handleLotsChange}
                    handleLotsIncrement={handleLotsIncrement}
                    handleLotsDecrement={handleLotsDecrement}
                    availableMargin={parsedUserData?.client_account?.available_margin}
                    isLoading={isLoading}
                    onSwipeComplete={handleSwipeComplete}
                />

                <OrderFooter
                    buttonType={currentData.buttonType}
                    intraday={marginDataLive}
                    overNight={marginOvernightDataLive}
                    isLoading={isLoading}
                    onSwipeComplete={handleSwipeComplete}
                />

                <OrderSuccessDialog
                    open={orderPlacedDialog}
                    onClose={handleClose}
                    orderData={saveOrder}
                    warning={orderWarning}
                />


            </div>

            <ToastContainer />
        </div>
    );
}

export default AddOrder;